import { useQuery, useMutation } from 'react-query';
import { queryGet, queryUpdate } from './fetch';
import { Session } from './session';
import { Panel } from './panel';
import { useGroup } from '@app/analytics';

export interface InvitedOrganizationMember {
  id: string;
  email: string;
  organizationId: string;
  organizationName: string;
  validUntil: string;
  account?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    isAlreadyClient?: boolean;
  };
}

export interface Organization {
  id: string;
  name: string;
  panels: Panel[];
  sessions?: Session[];
  ownerId: string;
}

export const useOrganization = (
  id?: string,
  { retry }: { retry?: boolean | number } = {},
) => {
  const group = useGroup();
  return useQuery<Organization>(
    ['organizations', id],
    queryGet(`/v2/organizations/${id}`),
    {
      enabled: !!id,
      staleTime: 1000 * 60, // 1 min
      retry,
      onSuccess: (data) => {
        group(data.id, data);
      },
    },
  );
};

export const useOrganizations = (ids?: string[]) => {
  return useQuery(
    ['organizationss', ids],
    () => {
      if (!ids) {
        return;
      }
      return Promise.all(
        ids.map((id) => queryGet(`/v2/organizations/${id}`)()),
      );
    },
    {
      enabled: !!ids,
    },
  );
};

export const useCheckOrganizationInvitation = (invitationId?: string) => {
  return useQuery<InvitedOrganizationMember>(
    ['organizationsInvitedValidity', invitationId],
    queryGet(`/v2/organizations/invitations/${invitationId}`),
    {
      enabled: !!invitationId,
      retry: false,
    },
  );
};

export const useAcceptInvitation = (invitationId?: string) => {
  return useMutation<InvitedOrganizationMember, Error, void>(
    queryUpdate(`/v2/organizations/invitations/${invitationId}/accept`),
  );
};
